/**
 * Phase2CIAndDeployBody (extracted from ReleaseOpsTab.jsx in iter84.59.19)
 *
 * Body of the Phase 2 ("CI & Deploy") collapsible section. The wrapper
 * `<CollapsiblePhaseSection number="2" ...>` stays in the orchestrator
 * so call-site tests pinning `number="2"` continue to pass; only the
 * children move here.
 *
 * Renders (in order):
 *   1. Recent Deploys card (GitHub CI status surface) — drives `ciVerdict`.
 *   2. CI green gate (`pre_deploy:ci_green`).
 *
 * The verdict surface is LIFTED to the orchestrator (via the `ci*`
 * callbacks in the ctx) so the Phase-2 status pill on the
 * CollapsiblePhaseSection wrapper can reflect it.
 *
 * Touched in iter84.59.19 only to relocate — zero behavior change.
 * iter86.35 — Open Pull Requests card AND the code-review gate moved to
 * Phase 3 (sub-step 3.3): Phase 2 is the Emergent preview → beta deploy
 * where no reviewable diff exists; review travels with the dev → main
 * PROMOTE PR (`post_deploy:pr_approved`).
 */
import { History } from "lucide-react";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import { InlineGateCard } from "@/components/site-admin/RunbookPhaseCards";
import {
    RecentDeploysCardContent,
} from "@/components/site-admin/release-ops/CIStatusCards";
import { checkOf } from "@/components/site-admin/release-ops/CollapsiblePhaseSection";
import { SubStep } from "@/components/site-admin/release-ops/Phase1PreCommitSafetyBody";

export function Phase2CIAndDeployBody({
    gateCtx,
    activePromotion,
    ciVerdict,
    setCiVerdict,
}) {
    return (
        <>
            <SubStep n={1} title="CI on the shipped SHA" phase={2} accentClass="text-fuchsia-400">
            {/* Recent Deploys (GitHub) — moved here from Phase 1
              * because CI status describes the COMMITTED code, not
              * the source-code safety the gate enforces above. */}
            <CollapsibleCard
                testId="recent-deploys-card"
                icon={History}
                title="CI Check — Recent Commits"
                badge="GitHub"
                badgeClass="bg-zinc-700/30 text-zinc-300 border-zinc-600"
                verdict={ciVerdict}
            >
                <RecentDeploysCardContent onVerdictChange={setCiVerdict} />
            </CollapsibleCard>

            {/* CI gate — anchored to GitHub recent deploys above. */}
            <InlineGateCard
                title="CI pipeline green on target SHA"
                description="Critical Suites + Backend Tests must be green on the SHA you're shipping."
                {...gateCtx}
                section="pre_deploy"
                itemId="ci_green"
                label="CI green"
                required
                state={checkOf(activePromotion, "pre_deploy", "ci_green")}
            />
            </SubStep>
        </>
    );
}
